import {getTurns, reconcilePersistedTimelineState} from '@github-ui/ahp-relay/ahp-session-selectors'
import {calculateTurnCredits, normalizeTokenPrices} from '@github-ui/copilot-shared-ui/utils/model-pricing'
import {useMemo} from 'react'
import type {ChatState} from '@microsoft/agent-host-protocol'
import type {CopilotChatModel} from '@github-ui/copilot-chat-types'

import {TokenUsageMeterSurface} from '../shared/TokenUsageMeterSurface'
import {useNativeAhpTimelineFallbackState} from '@github-ui/copilot-shared-ui/hooks/use-native-ahp-timeline-state'
import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import {useOptionalEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'

type TurnUsage = {
  usage?: {
    inputTokens?: number
    outputTokens?: number
    cacheReadTokens?: number
    model?: string
  }
}

export function calculateSessionUsage(
  turns: TurnUsage[],
  models:
    | Array<{
        id: string
        billing?: {token_prices?: NonNullable<CopilotChatModel['billing']>['token_prices']}
      }>
    | undefined,
): {inputTokens: number; outputTokens: number; totalCredits: number | null} | null {
  const modelMap = new Map((models ?? []).map(model => [model.id, model]))

  let inputTokens = 0
  let outputTokens = 0
  let totalCredits = 0
  let hasUsage = false
  let allUsageTurnsPriced = true

  for (const turn of turns) {
    const usage = turn.usage
    if (!usage) continue

    hasUsage = true
    inputTokens += usage.inputTokens ?? 0
    outputTokens += usage.outputTokens ?? 0

    if (!usage.model) {
      allUsageTurnsPriced = false
      continue
    }

    const pricing = normalizeTokenPrices(modelMap.get(usage.model)?.billing?.token_prices)
    if (!pricing) {
      allUsageTurnsPriced = false
      continue
    }

    totalCredits += calculateTurnCredits(
      {
        inputTokens: usage.inputTokens ?? 0,
        outputTokens: usage.outputTokens ?? 0,
        cacheReadTokens: usage.cacheReadTokens,
      },
      pricing,
    )
  }

  if (!hasUsage) return null

  const roundedTotalCredits = Math.round(totalCredits * 100) / 100

  return {
    inputTokens,
    outputTokens,
    totalCredits: allUsageTurnsPriced ? roundedTotalCredits : null,
  }
}

export function resolveSessionUsageState(relayState: ChatState | undefined, localState: ChatState | undefined) {
  return reconcilePersistedTimelineState(localState, relayState)
}

export function TokenUsageMeter({models}: {models: CopilotChatModel[] | undefined}) {
  const establishedSession = useOptionalEstablishedAhpSession()
  const establishedView = useOptionalEstablishedAhpSessionViewState()
  const {state: localState} = useNativeAhpTimelineFallbackState()
  const relayState = establishedSession ? establishedView.state : localState

  return <SessionTokenUsageMeter models={models} state={relayState} persistedState={localState} />
}

export function SessionTokenUsageMeter({
  models,
  state,
  persistedState,
}: {
  models: CopilotChatModel[] | undefined
  state: ChatState | undefined
  persistedState?: ChatState
}) {
  const sessionUsage = useMemo(() => {
    const sessionState = resolveSessionUsageState(state, persistedState)
    if (!sessionState) return null

    return calculateSessionUsage(getTurns(sessionState), models)
  }, [models, persistedState, state])

  return <TokenUsageMeterSurface sessionUsage={sessionUsage} />
}
